/**
 * Привязка подсистем к аккаунту (T5.4): чистая часть.
 *
 * Подсистема, торгующая сама (automaker, follow, lock, стратегии), торгует
 * от имени ОДНОГО назначенного аккаунта (Д-6). Не назначен — не торгует вовсе:
 * умолчания «единственный аккаунт» нет (инвариант 4 roadmap-accounts).
 */

import type {
  StrategySubsystem,
  Subsystem,
  SubsystemAccounts,
  SubsystemBinding,
} from "../api/types";

export type AnySubsystem = Subsystem | StrategySubsystem;

const STRATEGIES: readonly StrategySubsystem[] = ["decided", "family", "mispriced", "consensus"];

/** Стратегия ли это: у них привязка лежит в strategies, а не полем ответа. */
export function isStrategySubsystem(sub: string): sub is StrategySubsystem {
  return (STRATEGIES as readonly string[]).includes(sub);
}

/** Привязка подсистемы из ответа; null — ответа ещё нет. */
export function bindingOf(
  data: SubsystemAccounts | null | undefined,
  sub: AnySubsystem,
): SubsystemBinding | null {
  if (!data) return null;
  if (isStrategySubsystem(sub)) return data.strategies[sub] ?? { account: null };
  return data[sub];
}

export interface BindingView {
  /** Что показать рядом с выбором. */
  label: string;
  /** Торгует ли подсистема, по словам трейдера; null — трейдер не ответил. */
  trading: boolean | null;
}

/**
 * Подпись привязки.
 *
 * Нота трейдера важнее назначенного в Redis: торгует то, что видит трейдер, —
 * привязка к аккаунту, который он не загрузил, назначена, но не торгует, и
 * панель обязана сказать именно это.
 */
export function bindingView(b: SubsystemBinding | null): BindingView {
  if (!b) return { label: "привязка неизвестна", trading: null };
  const note = b.note?.trim();
  if (note) {
    if (note.startsWith("не ")) return { label: `не торгует: ${note}`, trading: false };
    // Осушаемый аккаунт продаёт, но не покупает (Д-2): «не торгует» было бы
    // неправдой о закрытии позиций.
    if (note.includes("осушается")) return { label: `только продажа: ${note}`, trading: true };
    return { label: `торгует от ${note}`, trading: true };
  }
  if (!b.account) return { label: "не назначен: не торгует", trading: false };
  return { label: "трейдер не ответил", trading: null };
}
